import { useTranslation } from"react-i18next";
import { useState, useEffect } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { usePagination, TablePagination } from"@/components/TablePagination";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Input } from"@/components/ui/input";
import { Label } from"@/components/ui/label";
import { Textarea } from"@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from"@/components/ui/tabs";
import { Plus, FileText, CheckCircle, XCircle, DollarSign, Upload, Calendar, HardHat, Download, ChevronDown, ChevronRight, Paperclip } from"lucide-react";
import { toast } from"sonner";

const InvoiceManagementPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [projects, setProjects] = useState([]);
 const [selectedProject, setSelectedProject] = useState("");
 const [partners, setPartners] = useState([]);
 const [subcontractors, setSubcontractors] = useState([]);
 const [invoices, setInvoices] = useState([]);
 const [contractorInvoices, setContractorInvoices] = useState([]);
 const [contractorProjectFilter, setContractorProjectFilter] = useState("all");
 const [collapsedCompanies, setCollapsedCompanies] = useState({});
 const [loading, setLoading] = useState(false);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [detailOpen, setDetailOpen] = useState(false);
 const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
 const [selectedInvoice, setSelectedInvoice] = useState(null);
 const [activeTab, setActiveTab] = useState(
 (user.role === "accountant" || user.role === "engineer") ? "contractor" : "partner"
 );
 const [formData, setFormData] = useState({
 partner_id:"",
 subcontractor_id:"",
 invoice_number:"",
 invoice_date: new Date().toISOString().split('T')[0],
 due_date:"",
 amount: 0,
 currency:"EUR",
 description:"",
 file_url:""
 });
 const [paymentData, setPaymentData] = useState({
 amount: 0,
 payment_date: new Date().toISOString().split('T')[0],
 payment_method:"",
 notes:""
 });
 const [extracting, setExtracting] = useState(false);
 const [extractedData, setExtractedData] = useState(null);

 const isPartner = user.role ==="contractor";
 const isOwner = user.role ==="owner"|| user.role ==="management";
 const canApproveInvoices = isOwner || user.role ==="engineer"|| user.role ==="accountant";

 useEffect(() => {
 loadProjects();
 if (canApproveInvoices) {
 loadContractorInvoices();
 }
 }, []);

 useEffect(() => {
 if (selectedProject) {
 loadInvoices();
 loadSubcontractors(); // Load active subcontractors for this project
 }
 }, [selectedProject]);

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 if (response.data.length > 0) {
 setSelectedProject(response.data[0].id);
 }
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const loadContractorInvoices = async () => {
 try {
 const response = await axios.get("/contractor-invoices/pending");
 setContractorInvoices(response.data);
 } catch (error) {
 console.error("Failed to load contractor invoices", error);
 }
 };

 const approveContractorInvoice = async (invoiceId, action) => {
 try {
 await axios.patch(`/contractor-invoices/${invoiceId}/approve`, { action });
 toast.success(action ==="approve"?"Invoice approved":"Invoice rejected");
 loadContractorInvoices();
 } catch (error) {
 toast.error(error.response?.data?.detail || `Failed to ${action} invoice`);
 }
 };

 const loadSubcontractors = async () => {
 try {
 const response = await axios.get(`/subcontractors/${selectedProject}`);
 // Only show active subcontractors
 const activeSubcontractors = response.data.filter(sub => sub.status === 'active');
 setSubcontractors(activeSubcontractors);
 } catch (error) {
 console.error("Failed to load subcontractors", error);
 setSubcontractors([]);
 }
 };

 const loadInvoices = async () => {
 setLoading(true);
 try {
 const response = await axios.get(`/invoices/${selectedProject}`);
 setInvoices(response.data);
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 } finally {
 setLoading(false);
 }
 };

 const handleFileUpload = async (e) => {
 const file = e.target.files[0];
 if (!file) return;

 const uploadFormData = new FormData();
 uploadFormData.append('file', file);

 try {
 // Upload the file
 toast.info("Uploading file...");
 const uploadResponse = await axios.post('/upload', uploadFormData);
 setFormData(prev => ({ ...prev, file_url: uploadResponse.data.url }));
 toast.success(t("messages.saveSuccess"));
 
 } catch (error) {
 toast.error(t("messages.loadFailed"));
 }
 };

 const handleExtractData = async () => {
 // Get the current file input
 const fileInput = document.getElementById('file');
 const file = fileInput?.files[0];
 
 if (!file) {
 toast.error(t("validation.required"));
 return;
 }
 
 // Check if file is a supported type (now including PDF)
 const fileName = file.name.toLowerCase();
 const isImage = file.type.startsWith('image/') || 
 fileName.endsWith('.jpg') || 
 fileName.endsWith('.jpeg') || 
 fileName.endsWith('.png') ||
 fileName.endsWith('.webp');
 const isPdf = file.type === 'application/pdf' || fileName.endsWith('.pdf');
 
 if (!isImage && !isPdf) {
 toast.error(t("validation.required"));
 return;
 }
 
 setExtracting(true);
 const loadingMsg = isPdf 
 ?"🤖 AI is processing the PDF... This may take a moment."
 :"🤖 AI is reading the invoice...";
 toast.info(loadingMsg);
 
 try {
 const extractFormData = new FormData();
 extractFormData.append('file', file);
 
 const extractResponse = await axios.post('/invoices/extract', extractFormData, {
 headers: { 'Content-Type': 'multipart/form-data' },
 timeout: 60000 // 60 second timeout for PDF processing
 });
 
 const extracted = extractResponse.data;
 setExtractedData(extracted);
 
 // Auto-fill form fields with extracted data
 setFormData(prev => ({
 ...prev,
 invoice_number: extracted.invoice_number || prev.invoice_number,
 invoice_date: extracted.invoice_date || prev.invoice_date,
 due_date: extracted.due_date || prev.due_date,
 amount: extracted.amount || prev.amount,
 currency: extracted.currency || prev.currency,
 description: extracted.description || prev.description
 }));
 
 const sourceType = extracted.source_type === 'pdf' ? ' (from PDF)' : '';
 toast.success(`✨ Invoice data extracted${sourceType}! Please verify the information.`);
 
 } catch (extractError) {
 console.error("Extraction error:", extractError);
 const errorMsg = extractError.response?.data?.detail ||"Could not auto-extract invoice data";
 toast.error(errorMsg);
 } finally {
 setExtracting(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 try {
 await axios.post("/invoices", {
 ...formData,
 project_id: selectedProject,
 invoice_date: new Date(formData.invoice_date).toISOString(),
 due_date: new Date(formData.due_date).toISOString()
 });
 toast.success(t("messages.saveSuccess"));
 setDialogOpen(false);
 setFormData({
 partner_id:"",
 subcontractor_id:"",
 invoice_number:"",
 invoice_date: new Date().toISOString().split('T')[0],
 due_date:"",
 amount: 0,
 currency:"EUR",
 description:"",
 file_url:""
 });
 loadInvoices();
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const updateStatus = async (invoiceId, status) => {
 try {
 const response = await axios.patch(`/invoices/${invoiceId}/status`, { status });
 toast.success(response.data.message);
 if (response.data.budget_updated) {
 toast.info("Invoice added to budget as actual cost");
 }
 loadInvoices();
 } catch (error) {
 toast.error(t("messages.saveFailed"));
 }
 };

 const handlePaymentSubmit = async (e) => {
 e.preventDefault();
 try {
 const response = await axios.post(`/invoices/${selectedInvoice.id}/payment`, {
 ...paymentData,
 payment_date: new Date(paymentData.payment_date).toISOString()
 });
 toast.success(response.data.message);
 setPaymentDialogOpen(false);
 setPaymentData({
 amount: 0,
 payment_date: new Date().toISOString().split('T')[0],
 payment_method:"",
 notes:""
 });
 loadInvoices();
 // Update selected invoice
 const updated = await axios.get(`/invoices/${selectedProject}`);
 const updatedInvoice = updated.data.find(inv => inv.id === selectedInvoice.id);
 if (updatedInvoice) setSelectedInvoice(updatedInvoice);
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const deleteInvoice = async (invoiceId) => {
 if (!window.confirm("Are you sure you want to delete this invoice? This will also remove the associated budget entry.")) return;
 try {
 const response = await axios.delete(`/invoices/${invoiceId}`);
 toast.success(response.data.message);
 if (response.data.budget_entries_removed > 0) {
 toast.info(`Removed ${response.data.budget_entries_removed} budget entry(s)`);
 }
 loadInvoices();
 } catch (error) {
 toast.error(t("messages.deleteFailed"));
 }
 };

 const deletePayment = async (invoiceId, paymentIndex) => {
 if (!window.confirm("Are you sure you want to delete this payment?")) return;
 try {
 const response = await axios.delete(`/invoices/${invoiceId}/payment/${paymentIndex}`);
 toast.success(response.data.message);
 loadInvoices();
 // Update selected invoice
 const updated = await axios.get(`/invoices/${selectedProject}`);
 const updatedInvoice = updated.data.find(inv => inv.id === invoiceId);
 if (updatedInvoice) setSelectedInvoice(updatedInvoice);
 } catch (error) {
 toast.error(error.response?.data?.detail || t("messages.saveFailed"));
 }
 };

 const openInvoiceDetail = (invoice) => {
 setSelectedInvoice(invoice);
 setDetailOpen(true);
 };

 const formatCurrency = (amount, currency ="EUR") => {
 return new Intl.NumberFormat('en-US', {
 style: 'currency',
 currency: currency,
 minimumFractionDigits: 2
 }).format(amount || 0);
 };

 const formatDate = (dateString) => {
 if (!dateString) return"N/A";
 return new Date(dateString).toLocaleDateString('en-GB', {
 day: '2-digit',
 month: 'short',
 year: 'numeric'
 });
 };

 const getStatusBadge = (status) => {
 const styles = {
 pending:"bg-amber-500/10 text-amber-700 border-yellow-200/60",
 accepted:"bg-emerald-500/10 text-emerald-700 border-green-300",
 rejected:"bg-rose-500/10 text-rose-700 border-red-300",
 partially_paid:"bg-blue-500/10 text-blue-700 border-blue-300",
 paid:"bg-emerald-500/10 text-emerald-700 border-green-300"
 };
 return styles[status] || styles.pending;
 };

 const getStatusColor = (status) => {
 const styles = {
 pending:"bg-amber-500/10 text-amber-700 border-yellow-200/60",
 accepted:"bg-emerald-500/10 text-emerald-700 border-green-300",
 rejected:"bg-rose-500/10 text-rose-700 border-red-300",
 partially_paid:"bg-blue-500/10 text-blue-700 border-blue-300",
 paid:"bg-emerald-500/10 text-emerald-700 border-green-300"
 };
 return styles[status] || styles.pending;
 };

 const getStatusIcon = (status) => {
 if (status ==="accepted"|| status ==="paid") return <CheckCircle className="w-4 h-4"/>;
 if (status ==="rejected") return <XCircle className="w-4 h-4"/>;
 return <FileText className="w-4 h-4"/>;
 };

 // Filter invoices based on user role
 const filteredInvoices = isPartner 
 ? invoices.filter(inv => inv.created_by === user.id)
 : invoices;

 const { currentPage: invPage, setCurrentPage: setInvPage, totalPages: invTotalPages, paginatedItems: paginatedInvoices, totalItems: invTotalItems, startIndex: invStart, endIndex: invEnd } = usePagination(filteredInvoices);

 const getPartnerName = (partnerId) => {
 const partner = partners.find(p => p.id === partnerId);
 return partner ? partner.company_name :"Unknown";
 };

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6 p-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-[#0F172A]">
 Invoice Management
 </h1>
 <p className="text-[#94A3B8] mt-1">
 {isPartner ?"Create and track your invoices":"Manage partner and contractor invoices"}
 </p>
 </div>
 </div>

 {/* Main Tabs for Owner/Engineer/Accountant */}
 {canApproveInvoices ? (
 <Tabs value={activeTab} onValueChange={setActiveTab}>
 <TabsList className="mb-4">
 {isOwner && (
 <TabsTrigger value="partner"className="flex items-center gap-2">
 <FileText className="w-4 h-4"/>
 Partner Invoices
 </TabsTrigger>
 )}
 <TabsTrigger value="contractor"className="flex items-center gap-2">
 <HardHat className="w-4 h-4"/>
 Contractor Invoices
 {contractorInvoices.length > 0 && (
 <Badge variant="destructive"className="ml-1 h-5 px-1.5 text-xs">{contractorInvoices.length}</Badge>
 )}
 </TabsTrigger>
 </TabsList>

 {/* Partner Invoices Tab */}
 <TabsContent value="partner">
 <div className="flex justify-end mb-4 gap-3">
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64">
 <SelectValue placeholder="Select Project"/>
 </SelectTrigger>
 <SelectContent>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 <Button onClick={() => setDialogOpen(true)}>
 <Plus className="w-4 h-4 mr-2"/>
 Create Invoice
 </Button>
 </div>

 {/* Summary Cards */}
 <div className="grid grid-cols-4 gap-4 mb-6">
 <Card>
 <CardContent className="pt-6">
 <div className="text-sm text-[#475569] mb-1">Pending Review</div>
 <div className="text-2xl font-bold">{filteredInvoices.filter(i => i.status ==="pending").length}</div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="pt-6">
 <div className="text-sm text-[#475569] mb-1">Accepted</div>
 <div className="text-2xl font-bold">{filteredInvoices.filter(i => i.status ==="accepted").length}</div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="pt-6">
 <div className="text-sm text-[#475569] mb-1">Partially Paid</div>
 <div className="text-2xl font-bold">{filteredInvoices.filter(i => i.status ==="partially_paid").length}</div>
 </CardContent>
 </Card>
 <Card>
 <CardContent className="pt-6">
 <div className="text-sm text-[#475569] mb-1">Fully Paid</div>
 <div className="text-2xl font-bold text-[#0F172A]">{filteredInvoices.filter(i => i.status ==="paid").length}</div>
 </CardContent>
 </Card>
 </div>

 {/* Partner Invoices List */}
 <Card>
 <CardHeader>
 <CardTitle>Partner Invoices</CardTitle>
 </CardHeader>
 <CardContent>
 {loading ? (
 <div className="text-center py-12 text-[#94A3B8]">Loading invoices...</div>
 ) : filteredInvoices.length === 0 ? (
 <div className="text-center py-12">
 <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-[#94A3B8]">No partner invoices yet</p>
 </div>
 ) : (
 <>
 <div className="space-y-3">
 {paginatedInvoices.map((invoice) => (
 <div
 key={invoice.id}
 className="border rounded-lg p-4 hover:bg-white/80 transition-colors cursor-pointer"
 onClick={() => openInvoiceDetail(invoice)}
 >
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-3 mb-2">
 <h3 className="text-lg font-semibold text-[#0F172A]">Invoice #{invoice.invoice_number}</h3>
 <Badge className={getStatusBadge(invoice.status)}>
 <span className="flex items-center gap-1">
 {getStatusIcon(invoice.status)}
 {invoice.status}
 </span>
 </Badge>
 </div>
 <div className="grid grid-cols-3 gap-x-6 gap-y-2 text-sm">
 <div>
 <span className="text-[#475569]">Amount: </span>
 <span className="font-bold text-blue-700">{formatCurrency(invoice.amount, invoice.currency)}</span>
 </div>
 <div>
 <span className="text-[#475569]">Due Date: </span>
 <span className="text-[#0F172A]">{formatDate(invoice.due_date)}</span>
 </div>
 <div>
 <span className="text-[#475569]">Paid: </span>
 <span className="font-semibold text-green-700">{formatCurrency(invoice.total_paid || 0, invoice.currency)}</span>
 </div>
 <div className="col-span-3">
 <span className="text-[#475569]">Description: </span>
 <span className="text-[#0F172A]">{invoice.description}</span>
 </div>
 </div>
 </div>
 {isOwner && invoice.status ==="pending"&& (
 <div className="flex gap-2 ml-4">
 <Button
 size="sm"
 onClick={(e) => {
 e.stopPropagation();
 updateStatus(invoice.id,"accepted");
 }}
 className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300"
 >
 <CheckCircle className="w-4 h-4 mr-1"/>
 Accept
 </Button>
 <Button
 size="sm"
 variant="outline"
 onClick={(e) => {
 e.stopPropagation();
 updateStatus(invoice.id,"rejected");
 }}
 className="hover:bg-rose-50/80 hover:text-rose-700 hover:border-rose-300"
 >
 <XCircle className="w-4 h-4 mr-1"/>
 Reject
 </Button>
 </div>
 )}
 </div>
 </div>
 ))}
 </div>
 <TablePagination currentPage={invPage} totalPages={invTotalPages} totalItems={invTotalItems} startIndex={invStart} endIndex={invEnd} onPageChange={setInvPage} />
 </>
 )}
 </CardContent>
 </Card>
 </TabsContent>

 {/* Contractor Invoices Tab */}
 <TabsContent value="contractor">
 <Card>
 <CardHeader>
 <div className="flex items-center justify-between flex-wrap gap-3">
 <CardTitle className="flex items-center gap-2">
 <HardHat className="w-5 h-5 text-orange-500"/>
 Contractor Invoices
 {contractorInvoices.filter(i => i.status === "pending_accountant").length > 0 && (
 <Badge variant="outline"className="ml-2 bg-orange-100 text-orange-700">
 {contractorInvoices.filter(i => i.status === "pending_accountant").length} awaiting approval
 </Badge>
 )}
 </CardTitle>
 {/* Project filter */}
 <Select value={contractorProjectFilter} onValueChange={setContractorProjectFilter}>
 <SelectTrigger className="w-52 rounded-sm h-8 text-xs">
 <SelectValue placeholder="All Projects"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All Projects</SelectItem>
 {[...new Map(contractorInvoices.map(i => [i.project_id, i])).values()]
 .filter(i => i.project_id)
 .map(i => (
 <SelectItem key={i.project_id} value={i.project_id}>
 {/* Show project name from the invoice's project */}
 {projects.find(p => p.id === i.project_id)?.name || i.project_id?.slice(0,8)}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 </CardHeader>
 <CardContent>
 {(() => {
 const filtered = contractorProjectFilter === "all"
 ? contractorInvoices
 : contractorInvoices.filter(i => i.project_id === contractorProjectFilter);

 if (filtered.length === 0) return (
 <div className="text-center py-12">
 <HardHat className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-500">No contractor invoices{contractorProjectFilter !== "all" ? " for this project" : ""}</p>
 </div>
 );

 // Group by partner_name
 const grouped = {};
 filtered.forEach(inv => {
 const key = inv.partner_name || "Unknown";
 if (!grouped[key]) grouped[key] = [];
 grouped[key].push(inv);
 });

 return (
 <div className="space-y-4">
 {Object.entries(grouped).map(([companyName, invoices]) => {
 const pendingCount = invoices.filter(i => i.status === "pending_accountant").length;
 const totalApproved = invoices.filter(i => i.status === "approved").reduce((s,i) => s + (i.total_amount||0), 0);
 const totalPending = invoices.filter(i => ["pending_engineer","pending_accountant"].includes(i.status)).reduce((s,i) => s + (i.total_amount||0), 0);
 const isCollapsed = collapsedCompanies[companyName];

 return (
 <div key={companyName} className="border border-slate-200 rounded-sm overflow-hidden">
 {/* Collapsible company header */}
 <button
 onClick={() => setCollapsedCompanies(prev => ({ ...prev, [companyName]: !prev[companyName] }))}
 className="w-full bg-slate-50 px-4 py-3 flex items-center justify-between border-b border-slate-200 hover:bg-slate-100 transition-colors text-left"
 >
 <div className="flex items-center gap-3">
 {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400"/> : <ChevronDown className="w-4 h-4 text-slate-400"/>}
 <HardHat className="w-4 h-4 text-orange-500"/>
 <span className="font-semibold text-slate-900">{companyName}</span>
 <span className="text-xs text-slate-400">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</span>
 {pendingCount > 0 && <Badge className="bg-amber-500 text-white text-xs py-0">{pendingCount} pending</Badge>}
 </div>
 <div className="flex gap-4 text-xs">
 {totalApproved > 0 && <span className="text-emerald-600 font-medium">Paid: €{totalApproved.toLocaleString()}</span>}
 {totalPending > 0 && <span className="text-amber-600 font-medium">Pending: €{totalPending.toLocaleString()}</span>}
 </div>
 </button>

 {/* Invoices (collapsible) */}
 {!isCollapsed && (
 <div className="divide-y divide-slate-100">
 {invoices.map(inv => (
 <div key={inv.id} className="p-4">
 <div className="flex items-start justify-between gap-4">
 <div className="flex-1 min-w-0 space-y-2">
 <div className="flex items-center gap-2 flex-wrap">
 <span className="font-semibold text-slate-900 font-mono text-sm">{inv.invoice_number}</span>
 <Badge variant="outline" className={
 inv.status === "pending_engineer" ? "bg-amber-50 text-amber-700 border-amber-200 text-xs" :
 inv.status === "pending_accountant" ? "bg-blue-50 text-blue-700 border-blue-200 text-xs" :
 inv.status === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200 text-xs" :
 "bg-red-50 text-red-700 border-red-200 text-xs"
 }>
 {inv.status === "pending_engineer" ? "Pending Engineer" :
 inv.status === "pending_accountant" ? "Pending Your Approval" :
 inv.status === "approved" ? "Approved" : "Rejected"}
 </Badge>
 {inv.engineer_approved && (
 <span className="text-xs text-emerald-600 flex items-center gap-0.5">
 <CheckCircle className="w-3 h-3"/> Engineer approved
 </span>
 )}
 </div>

 {(inv.linked_report_title || inv.milestone_label) && (
 <div className="bg-slate-50 border border-slate-200 rounded-sm px-3 py-2 text-sm">
 <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Work Invoiced</p>
 <p className="font-medium text-slate-800">{inv.linked_report_title || inv.milestone_label}</p>
 {inv.m2_completed && (
 <p className="text-xs text-slate-500 mt-0.5">
 {inv.m2_completed} {inv.material_unit_of_measure || "m\u00B2"}
 {inv.price_per_m2_used ? <span className="text-emerald-600"> × €{inv.price_per_m2_used}/{inv.material_unit_of_measure || "m\u00B2"} = <strong>€{(inv.m2_completed * inv.price_per_m2_used).toLocaleString()}</strong></span> : null}
 </p>
 )}
 {inv.report_material_total && inv.report_material_total > 0 && (
 <p className="text-[10px] text-blue-600 mt-0.5">
 Verified from approved report ({inv.report_material_total} {inv.material_unit_of_measure || "m\u00B2"})
 </p>
 )}
 </div>
 )}

 <div className="flex flex-wrap gap-4 text-sm">
 <div><span className="text-xs text-slate-400">Amount</span><p className="font-semibold text-slate-900">€{(inv.amount||0).toLocaleString()}</p></div>
 {inv.tax_amount > 0 && <div><span className="text-xs text-slate-400">Tax</span><p className="font-semibold text-slate-900">€{inv.tax_amount.toLocaleString()}</p></div>}
 <div><span className="text-xs text-slate-400">Total</span><p className="font-bold text-slate-900 text-base">€{(inv.total_amount||0).toLocaleString()}</p></div>
 {inv.due_date && <div><span className="text-xs text-slate-400">Due</span><p className="font-semibold text-slate-900">{new Date(inv.due_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</p></div>}
 </div>

 {inv.description && <p className="text-xs text-slate-500 italic">Notes: {inv.description}</p>}
 <p className="text-[11px] text-slate-400">Submitted {new Date(inv.created_at).toLocaleDateString()}</p>
 </div>

 <div className="flex flex-col gap-2 flex-shrink-0 items-end">
 {inv.invoice_attachments?.length > 0 && (
 <a href={inv.invoice_attachments[0]} target="_blank" rel="noopener noreferrer"
 className="flex items-center gap-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-sm font-medium whitespace-nowrap">
 <Download className="w-3.5 h-3.5"/> Download Invoice
 </a>
 )}
 {inv.status === "pending_accountant" && (
 <div className="flex gap-1.5">
 <button onClick={() => approveContractorInvoice(inv.id, "approve")}
 className="flex items-center gap-1 text-xs bg-emerald-500 hover:bg-emerald-600 text-white px-2.5 py-1.5 rounded-sm font-medium">
 <CheckCircle className="w-3 h-3"/> Approve
 </button>
 <button onClick={() => approveContractorInvoice(inv.id, "reject")}
 className="flex items-center gap-1 text-xs bg-red-500 hover:bg-red-600 text-white px-2.5 py-1.5 rounded-sm font-medium">
 <XCircle className="w-3 h-3"/> Reject
 </button>
 </div>
 )}
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 );
 })}
 </div>
 );
 })()}
 </CardContent>
 </Card>
 </TabsContent>
 </Tabs>
 ) : (
 /* Non-owner view - just show partner invoices */
 <>
 <div className="flex justify-end mb-4 gap-3">
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger className="w-64">
 <SelectValue placeholder="Select Project"/>
 </SelectTrigger>
 <SelectContent>
 {projects.map((project) => (
 <SelectItem key={project.id} value={project.id}>
 {project.name}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 <Button onClick={() => setDialogOpen(true)}>
 <Plus className="w-4 h-4 mr-2"/>
 Create Invoice
 </Button>
 </div>
 <Card>
 <CardHeader>
 <CardTitle>Invoices</CardTitle>
 </CardHeader>
 <CardContent>
 {loading ? (
 <div className="text-center py-12 text-[#94A3B8]">Loading invoices...</div>
 ) : filteredInvoices.length === 0 ? (
 <div className="text-center py-12">
 <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-[#94A3B8]">No invoices yet</p>
 </div>
 ) : (
 <>
 <div className="space-y-3">
 {paginatedInvoices.map((invoice) => (
 <div
 key={invoice.id}
 className="p-4 border border-white/60 rounded-lg hover:bg-white/80 cursor-pointer transition-colors"
 onClick={() => {
 setSelectedInvoice(invoice);
 setDetailOpen(true);
 }}
 >
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-3 mb-2">
 {getStatusIcon(invoice.status)}
 <span className="font-medium text-[#0F172A]">{invoice.invoice_number}</span>
 <Badge variant="outline"className={getStatusColor(invoice.status)}>
 {invoice.status}
 </Badge>
 </div>
 <div className="grid grid-cols-4 gap-4 text-sm">
 <div>
 <span className="text-[#475569]">Amount: </span>
 <span className="font-semibold text-[#0F172A]">{formatCurrency(invoice.amount, invoice.currency)}</span>
 </div>
 <div>
 <span className="text-[#475569]">Due: </span>
 <span className={`${new Date(invoice.due_date) < new Date() && invoice.status !=="paid"?"text-red-600":"text-[#0F172A]"}`}>
 {new Date(invoice.due_date).toLocaleDateString()}
 </span>
 </div>
 <div>
 <span className="text-[#475569]">Paid: </span>
 <span className="font-semibold text-green-700">{formatCurrency(invoice.total_paid || 0, invoice.currency)}</span>
 </div>
 <div className="col-span-3">
 <span className="text-[#475569]">Description: </span>
 <span className="text-[#0F172A]">{invoice.description}</span>
 </div>
 </div>
 </div>
 </div>
 </div>
 ))}
 </div>
 <TablePagination currentPage={invPage} totalPages={invTotalPages} totalItems={invTotalItems} startIndex={invStart} endIndex={invEnd} onPageChange={setInvPage} />
 </>
 )}
 </CardContent>
 </Card>
 </>
 )}

 {/* Create Invoice Dialog */}
 <Dialog open={dialogOpen} onOpenChange={(open) => {
 setDialogOpen(open);
 if (!open) {
 setExtractedData(null); // Clear extracted data when dialog closes
 }
 }}>
 <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Create New Invoice</DialogTitle>
 </DialogHeader>
 <form onSubmit={handleSubmit} className="space-y-4">
 <div>
 <Label htmlFor="subcontractor_id">Subcontractor *</Label>
 <Select
 value={formData.subcontractor_id}
 onValueChange={(value) => {
 const selected = subcontractors.find(sub => sub.id === value);
 setFormData({ 
 ...formData, 
 subcontractor_id: value,
 partner_id: selected?.partner_id ||""
 });
 }}
 required
 >
 <SelectTrigger>
 <SelectValue placeholder="Select subcontractor from this project"/>
 </SelectTrigger>
 <SelectContent>
 {subcontractors.length === 0 ? (
 <div className="p-2 text-sm text-[#94A3B8]">
 No active subcontractors on this project
 </div>
 ) : (
 subcontractors.map((sub) => (
 <SelectItem key={sub.id} value={sub.id}>
 {sub.partner_details?.company_name ||"Unknown"} - {sub.service_type}
 {sub.contract_reference && ` (${sub.contract_reference})`}
 </SelectItem>
 ))
 )}
 </SelectContent>
 </Select>
 {subcontractors.length === 0 && (
 <p className="text-[11px] text-[#94A3B8] mt-1">
 First assign subcontractors to this project from the Subcontractors page
 </p>
 )}
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="invoice_number">Invoice Number *</Label>
 <Input
 id="invoice_number"
 value={formData.invoice_number}
 onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
 placeholder="INV-2025-001"
 required
 />
 </div>
 <div>
 <Label htmlFor="currency">{t("projectProgress.currency")}</Label>
 <Select
 value={formData.currency}
 onValueChange={(value) => setFormData({ ...formData, currency: value })}
 >
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="EUR">EUR</SelectItem>
 <SelectItem value="USD">USD</SelectItem>
 <SelectItem value="MKD">MKD</SelectItem>
 </SelectContent>
 </Select>
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <div>
 <Label htmlFor="invoice_date">Invoice Date *</Label>
 <Input
 id="invoice_date"
 type="date"
 value={formData.invoice_date}
 onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="due_date">Due Date *</Label>
 <Input
 id="due_date"
 type="date"
 value={formData.due_date}
 onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
 required
 />
 </div>
 </div>
 <div>
 <Label htmlFor="amount">Amount *</Label>
 <Input
 id="amount"
 type="number"
 step="0.01"
 value={formData.amount}
 onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
 required
 />
 </div>
 <div>
 <Label htmlFor="description">Description *</Label>
 <Textarea
 id="description"
 value={formData.description}
 onChange={(e) => setFormData({ ...formData, description: e.target.value })}
 rows={3}
 required
 />
 </div>
 <div>
 <Label htmlFor="file">Attach Invoice Document</Label>
 <div className="flex gap-2">
 <Input
 id="file"
 type="file"
 accept=".jpg,.jpeg,.png,.pdf"
 onChange={handleFileUpload}
 className="cursor-pointer flex-1"
 disabled={extracting}
 />
 {formData.file_url && (
 <Button
 type="button"
 variant="outline"
 onClick={handleExtractData}
 disabled={extracting}
 className="whitespace-nowrap"
 >
 {extracting ? '🤖 Extracting...' : '🤖 Extract with AI'}
 </Button>
 )}
 </div>
 <p className="text-[11px] text-[#94A3B8] mt-1">
 Upload PDF or image. After upload, click &quot;Extract with AI&quot; to auto-fill data (works with PDF, JPG, PNG, WebP).
 </p>
 {extracting && (
 <div className="mt-2 p-2 bg-blue-50/80 border border-blue-200/60 rounded text-sm text-blue-700">
 🤖 AI is reading the invoice... Please wait.
 </div>
 )}
 {extractedData && !extracting && (
 <div className="mt-2 p-3 bg-green-50/80 border border-green-200/60 rounded">
 <p className="text-sm font-semibold text-green-800 mb-2">✨ AI Extracted Data:</p>
 <div className="text-sm text-green-700 space-y-1">
 <div>• Invoice #: <span className="font-medium">{extractedData.invoice_number || 'Not found'}</span></div>
 <div>• Invoice Date: <span className="font-medium">{extractedData.invoice_date || 'Not found'}</span></div>
 <div>• Due Date: <span className="font-medium">{extractedData.due_date || 'Not found'}</span></div>
 <div>• Amount: <span className="font-medium">{extractedData.amount || 'Not found'} {extractedData.currency}</span></div>
 {extractedData.description && (
 <div>• Description: <span className="font-medium">{extractedData.description}</span></div>
 )}
 </div>
 <p className="text-xs text-green-600 mt-2">✓ All fields auto-filled. Please verify before submitting.</p>
 </div>
 )}
 {formData.file_url && !extracting && !extractedData && (
 <p className="text-sm text-green-600 mt-1">✓ File uploaded. Click"Extract with AI"or fill manually.</p>
 )}
 </div>
 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1">Create Invoice</Button>
 <Button type="button"variant="outline"onClick={() => setDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>

 {/* Invoice Detail Dialog */}
 <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
 <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
 <DialogHeader>
 <DialogTitle>Invoice Details</DialogTitle>
 </DialogHeader>
 {selectedInvoice && (
 <div className="space-y-6">
 {/* Invoice Header */}
 <div className="bg-white/50 p-4 rounded-lg">
 <div className="flex items-start justify-between mb-4">
 <div>
 <h2 className="text-2xl font-bold text-[#0F172A]">Invoice #{selectedInvoice.invoice_number}</h2>
 <p className="text-[#475569]">Partner: {getPartnerName(selectedInvoice.partner_id)}</p>
 </div>
 <Badge className={getStatusBadge(selectedInvoice.status)}>
 {selectedInvoice.status}
 </Badge>
 </div>
 <div className="grid grid-cols-3 gap-4 text-sm">
 <div>
 <p className="text-[#475569]">Invoice Date</p>
 <p className="font-semibold">{formatDate(selectedInvoice.invoice_date)}</p>
 </div>
 <div>
 <p className="text-[#475569]">Due Date</p>
 <p className="font-semibold">{formatDate(selectedInvoice.due_date)}</p>
 </div>
 <div>
 <p className="text-[#475569]">Currency</p>
 <p className="font-semibold">{selectedInvoice.currency}</p>
 </div>
 </div>
 </div>

 {/* Amount Summary */}
 <div className="bg-blue-50/80 p-4 rounded-lg border border-blue-200/60">
 <div className="grid grid-cols-3 gap-4">
 <div>
 <p className="text-sm text-[#94A3B8] font-semibold mb-1">Total Amount</p>
 <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency(selectedInvoice.amount, selectedInvoice.currency)}</p>
 </div>
 <div>
 <p className="text-sm text-[#94A3B8] font-semibold mb-1">Paid</p>
 <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency(selectedInvoice.total_paid || 0, selectedInvoice.currency)}</p>
 </div>
 <div>
 <p className="text-sm text-[#94A3B8] font-semibold mb-1">Outstanding</p>
 <p className="text-2xl font-bold text-[#0F172A]">{formatCurrency((selectedInvoice.amount - (selectedInvoice.total_paid || 0)), selectedInvoice.currency)}</p>
 </div>
 </div>
 </div>

 {/* Description */}
 <div>
 <p className="text-sm font-semibold text-[#334155] mb-2">Description</p>
 <p className="text-[#334155] bg-white/50 p-3 rounded">{selectedInvoice.description}</p>
 </div>

 {/* Document */}
 {selectedInvoice.file_url && (
 <div className="bg-blue-50/80 p-3 rounded border border-blue-200/60">
 <p className="text-sm font-semibold text-blue-900 mb-2">Invoice Document</p>
 <a
 href={selectedInvoice.file_url}
 target="_blank"
 rel="noopener noreferrer"
 className="text-blue-600 hover:underline flex items-center gap-2"
 >
 <FileText className="w-4 h-4"/>
 View Document
 </a>
 </div>
 )}

 {/* Payment History */}
 {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
 <div className="border rounded-lg p-4">
 <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
 <DollarSign className="w-5 h-5"/>
 Payment History
 </h3>
 <div className="space-y-2">
 {selectedInvoice.payments.map((payment, index) => (
 <div key={index} className="bg-white/50 p-3 rounded border">
 <div className="flex items-center justify-between">
 <div className="flex-1">
 <p className="font-semibold text-green-700">{formatCurrency(payment.amount, selectedInvoice.currency)}</p>
 <p className="text-xs text-[#475569]">
 {formatDate(payment.payment_date)}
 {payment.payment_method && ` • ${payment.payment_method}`}
 </p>
 {payment.notes && <p className="text-[11px] text-[#94A3B8] mt-1">{payment.notes}</p>}
 </div>
 {isOwner && (
 <Button
 variant="destructive"
 size="sm"
 onClick={() => deletePayment(selectedInvoice.id, index)}
 >
 Delete
 </Button>
 )}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Actions */}
 <div className="flex gap-3 pt-4 border-t">
 {isOwner && selectedInvoice.status ==="pending"&& (
 <>
 <Button
 onClick={() => {
 updateStatus(selectedInvoice.id,"accepted");
 setDetailOpen(false);
 }}
 className="hover:bg-emerald-50/80 hover:text-emerald-700 hover:border-emerald-300"
 >
 <CheckCircle className="w-4 h-4 mr-2"/>
 Accept Invoice
 </Button>
 <Button
 variant="outline"
 onClick={() => {
 updateStatus(selectedInvoice.id,"rejected");
 setDetailOpen(false);
 }}
 className="hover:bg-rose-50/80 hover:text-rose-700 hover:border-rose-300"
 >
 <XCircle className="w-4 h-4 mr-2"/>
 Reject Invoice
 </Button>
 </>
 )}
 {isOwner && (selectedInvoice.status ==="accepted"|| selectedInvoice.status ==="partially_paid") && (
 <Button
 onClick={() => setPaymentDialogOpen(true)}
 className="bg-slate-900 hover:bg-slate-800:bg-zinc-200"
 >
 <DollarSign className="w-4 h-4 mr-2"/>
 Record Payment
 </Button>
 )}
 <Button
 variant="destructive"
 onClick={() => {
 deleteInvoice(selectedInvoice.id);
 setDetailOpen(false);
 }}
 >
 Delete
 </Button>
 <Button variant="outline"onClick={() => setDetailOpen(false)} className="ml-auto">
 Close
 </Button>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>

 {/* Record Payment Dialog */}
 <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Record Payment</DialogTitle>
 </DialogHeader>
 <form onSubmit={handlePaymentSubmit} className="space-y-4">
 {selectedInvoice && (
 <div className="bg-blue-50/80 p-3 rounded border border-blue-200/60">
 <p className="text-sm text-blue-900 mb-1">Outstanding Amount</p>
 <p className="text-xl font-bold text-[#0F172A]">
 {formatCurrency((selectedInvoice.amount - (selectedInvoice.total_paid || 0)), selectedInvoice.currency)}
 </p>
 </div>
 )}
 <div>
 <Label htmlFor="payment_amount">Payment Amount *</Label>
 <Input
 id="payment_amount"
 type="number"
 step="0.01"
 value={paymentData.amount}
 onChange={(e) => setPaymentData({ ...paymentData, amount: parseFloat(e.target.value) })}
 required
 />
 </div>
 <div>
 <Label htmlFor="payment_date">Payment Date *</Label>
 <Input
 id="payment_date"
 type="date"
 value={paymentData.payment_date}
 onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
 required
 />
 </div>
 <div>
 <Label htmlFor="payment_method">Payment Method</Label>
 <Select
 value={paymentData.payment_method}
 onValueChange={(value) => setPaymentData({ ...paymentData, payment_method: value })}
 >
 <SelectTrigger>
 <SelectValue placeholder="Select method"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
 <SelectItem value="Check">Check</SelectItem>
 <SelectItem value="Cash">Cash</SelectItem>
 <SelectItem value="Credit Card">Credit Card</SelectItem>
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label htmlFor="payment_notes">Notes</Label>
 <Textarea
 id="payment_notes"
 value={paymentData.notes}
 onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
 rows={2}
 />
 </div>
 <div className="flex gap-3 pt-4 border-t">
 <Button type="submit"className="flex-1">Record Payment</Button>
 <Button type="button"variant="outline"onClick={() => setPaymentDialogOpen(false)} className="flex-1">
 Cancel
 </Button>
 </div>
 </form>
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default InvoiceManagementPage;
